import { Copy01Icon, Key01Icon, Tick02Icon, ViewIcon, ViewOffSlashIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { IconButton } from '../primitives/button.js';
import { controlBase } from '../primitives/input.js';

export interface CopyFieldProps {
  label: string;
  value: string;
  /** Secret shown once: masked by default with a reveal toggle and a "no se volverá a mostrar" notice. */
  secret?: boolean;
  description?: React.ReactNode;
  onCopied?: () => void;
  className?: string;
}

/** Read-only value with a copy button (webhook URLs, one-time secrets). */
export const CopyField = React.forwardRef<HTMLInputElement, CopyFieldProps>(function CopyField({ label, value, secret, description, onCopied, className }, ref) {
  const id = React.useId();
  const [copied, setCopied] = React.useState(false);
  const [revealed, setRevealed] = React.useState(!secret);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setFailed(false);
      setCopied(true);
      onCopied?.();
    } catch {
      setFailed(true);
    }
  };
  return (
    <div className={cn('grid gap-1.5', className)}>
      <label htmlFor={id} className="text-body font-medium text-text-primary">
        {label}
      </label>
      {description ? <p className="-mt-0.5 text-meta text-text-secondary">{description}</p> : null}
      <div className="flex items-center gap-1">
        <input
          ref={ref}
          id={id}
          readOnly
          type={revealed ? 'text' : 'password'}
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          className={cn(controlBase, 'bg-surface-subtle font-mono text-meta')}
        />
        {secret ? (
          <IconButton
            variant="secondary"
            label={revealed ? 'Ocultar valor' : 'Mostrar valor'}
            icon={revealed ? <Icon icon={ViewOffSlashIcon} aria-hidden /> : <Icon icon={ViewIcon} aria-hidden />}
            onClick={() => setRevealed((r) => !r)}
          />
        ) : null}
        <IconButton variant="secondary" label={copied ? 'Copiado' : `Copiar ${label.toLowerCase()}`} icon={copied ? <Icon icon={Tick02Icon} aria-hidden /> : <Icon icon={Copy01Icon} aria-hidden />} onClick={copy} />
      </div>
      <p aria-live="polite" className="min-h-4 text-meta">
        {copied ? <span className="text-success">Copiado al portapapeles.</span> : null}
        {failed ? <span className="text-danger">No se pudo copiar. Selecciona el texto y cópialo manualmente.</span> : null}
        {!copied && !failed && secret ? (
          <span className="inline-flex items-center gap-1 text-warning">
            <Icon icon={Key01Icon} aria-hidden className="size-3.5" />
            Guárdalo ahora: no se volverá a mostrar.
          </span>
        ) : null}
      </p>
    </div>
  );
});
